import axios from "axios";

import variables from "@/global/variables";
import session from "@/global/session";

/*
 * Client for the PHP bid endpoints (bid-state.php / bid-place.php). Reads are
 * public; placing a bid sends the signed session token in the X-Bidder-Token
 * header (never in the URL or body).
 */

export interface ItemState {
	itemNumber: number;
	name: string | null;
	category: string | null;
	description: string | null;
	value: number | null;
	imageUrl: string | null;
	openingPrice: number;
	increment: number;
	currentBid: number | null;
	minimumNextBid: number;
	bidCount: number;
	leadingBidNumber: number | null;
	youAreLeading: boolean;
	youHaveBid: boolean;
}

export interface AuctionState {
	auctionStatus: "open" | "closed";
	serverTime: string;
	// False when no token was sent OR the token was rejected (expired/revoked).
	authenticated: boolean;
	items: ItemState[];
}

export interface BidResult {
	accepted: boolean;
	reason?: string;
	itemNumber?: number;
	currentBid?: number | null;
	minimumNextBid?: number;
	youAreLeading?: boolean;
}

function tokenHeader() {
	const token = session.token();
	return token ? { "X-Bidder-Token": token } : {};
}

export default class BidModel {
	static async state(): Promise<AuctionState> {
		const response = await axios.get(variables.apiEndpoint + "/bid-state.php", {
			params: { _: Date.now() },
			headers: { ...tokenHeader() },
		});
		return response.data as AuctionState;
	}

	static async stateFor(itemNumber: number): Promise<AuctionState> {
		const response = await axios.get(variables.apiEndpoint + "/bid-state.php", {
			params: { itemNumber, _: Date.now() },
			headers: { ...tokenHeader() },
		});
		return response.data as AuctionState;
	}

	static async place(itemNumber: number, amount: number): Promise<BidResult> {
		try {
			const response = await axios.post(
				variables.apiEndpoint + "/bid-place.php",
				{ itemNumber, amount },
				{ headers: { "Content-Type": "application/json", ...tokenHeader() } }
			);
			return response.data as BidResult;
		} catch (error) {
			if (error.response && error.response.data) return error.response.data as BidResult;
			throw error;
		}
	}
}
